import {
  FC, memo, useEffect, useState,
} from 'react';
import { Text, Title, Table } from 'shared/ui';
import { UnFormalizedZipResponseDto } from 'shared/api';
import { attachmentApi } from 'entities/attachment/api/attachmentApi';
import { saveFile } from 'shared/utils/files';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import { getUnFormalizedZipsByEngineId, unFormalizedZipSelectors } from 'entities/unFormalizedZip';
import { unFormalizedZipActions } from 'entities/unFormalizedZip/model/slice/unFormalizedZip.slice';
import { UnFormalizedZipForm } from '../UnFormalizedZipForm';
import { UNFORMALIZED_ZIP_TABLE_COLUMNS } from '../../../constants/unFormalizedZipConstants';
import styles from './EngineUnFormalizedZipSection.module.scss';

interface EngineUnFormalizedZipSectionProps {
  engineId: number | null;
}

export const EngineUnFormalizedZipSection: FC<EngineUnFormalizedZipSectionProps> = memo(({
  engineId,
}) => {
  const unFormalizedZips = useAppSelector(unFormalizedZipSelectors.getUnFormalizedZips);
  const [isUnFormalizedZipFormOpen, setIsUnFormalizedZipFormOpen] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (engineId) {
      dispatch(getUnFormalizedZipsByEngineId(engineId));
    }
    return () => {
      dispatch(unFormalizedZipActions.dropDetailData());
    };
  }, [engineId]);

  const handleCreateUnFormalizedZip = () => {
    setIsUnFormalizedZipFormOpen(true);
  };

  const handleUnFormalizedZipFormClose = () => {
    setIsUnFormalizedZipFormOpen(false);
  };

  const handleUnFormalizedZipSuccess = () => {
    if (engineId) {
      dispatch(getUnFormalizedZipsByEngineId(engineId));
    }
  };

  const handleDownloadFile = async (uuid: string) => {
    try {
      const { downloadUrl } = await attachmentApi.getDownloadUrl(uuid);
      if (downloadUrl) {
        saveFile(downloadUrl, '');
      }
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
    }
  };

  const handleDownloadAllFiles = async (record: UnFormalizedZipResponseDto) => {
    if (record.docUrlUuids && record.docUrlUuids.length > 0) {
      // Скачиваем все файлы последовательно
      const downloadPromises = record.docUrlUuids.map(async (uuid, index) => {
        // Добавляем задержку между скачиваниями
        if (index > 0) {
          await new Promise((resolve) => {
            setTimeout(resolve, 500);
          });
        }
        return handleDownloadFile(uuid);
      });

      await Promise.all(downloadPromises);
    }
  };

  const actions = [
    {
      key: 'download',
      label: 'Скачать файлы',
      onClick: (record: UnFormalizedZipResponseDto) => {
        handleDownloadAllFiles(record);
      },
    },
  ];

  return (
    <div className={styles.unFormalizedZipSection}>
      <div className={styles.unFormalizedZipHeader}>
        <Title level={5}>Список ЗИП</Title>
        <button
          type="button"
          className={styles.createZipButton}
          onClick={handleCreateUnFormalizedZip}
        >
          Прикрепить имеющийся ЗИП
        </button>
      </div>

      {unFormalizedZips && unFormalizedZips.length > 0 ? (
        <Table
          columns={UNFORMALIZED_ZIP_TABLE_COLUMNS}
          dataSource={unFormalizedZips}
          hasPagination={false}
          actions={actions}
        />
      ) : (
        <Text>Списки запасных частей не найдены</Text>
      )}

      {isUnFormalizedZipFormOpen && engineId && (
        <UnFormalizedZipForm
          engineId={engineId!}
          onClose={handleUnFormalizedZipFormClose}
          onSuccess={handleUnFormalizedZipSuccess}
        />
      )}
    </div>
  );
});

EngineUnFormalizedZipSection.displayName = 'EngineUnFormalizedZipSection';
